import React, { useMemo, useState } from "react";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Calculator as CalcIcon, ArrowRight, Check, Loader2, CheckCircle2, Building2, AlertTriangle } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Pricing System V1.0 */
const SERVICES = {
  "Standard Cleaning": { kind: "residential", base: 250, tag: "from $250" },
  "Deep Cleaning": { kind: "residential", base: 350, tag: "from $350" },
  "Move-In / Move-Out Cleaning": { kind: "residential", base: 400, tag: "from $400" },
  "Commercial Cleaning": { kind: "sqft", rate: 0.3, min: 200, tag: "$0.30/sq ft" },
  "Carpet Cleaning": { kind: "unit", rate: 100, min: 100, unitLabel: "Number of Rooms", tag: "$100/room" },
  "Upholstery Cleaning": { kind: "unit", rate: 50, min: 50, unitLabel: "Number of Pieces", tag: "$50/piece" },
  "Floor Buffing / Waxing": { kind: "sqft", rate: 1.5, min: 300, tag: "$1.50/sq ft" },
  "Post-Construction Cleaning": { kind: "sqft", rate: 0.75, min: 500, tag: "$0.75/sq ft" },
  "Residential Window Cleaning": { kind: "flat", base: 200, tag: "$200" },
  "Commercial Window Cleaning": { kind: "unit", rate: 15, min: 250, unitLabel: "Number of Windows", tag: "$15/window" },
};
const RES_MIN = 150;
const TYPES = ["Apartment / Condo", "House", "Townhouse", "Office / Commercial Space"];
const CONDITIONS = { "Light — well maintained": 1.0, "Average": 1.1, "Heavy — needs extra attention": 1.25, "Very heavy / major buildup": 1.25 };
const FREQS = ["One-time", "Weekly", "Bi-weekly", "Monthly"];
const FREQ_DISCOUNT = { "Weekly": 0.1, "Bi-weekly": 0.05 };
const ADDONS = {
  "Inside Refrigerator": 50, "Inside Oven": 50, "Interior Cabinet Cleaning": 75,
  "Window Sills & Tracks": 50, "Wall Spot Cleaning (per hour)": 75,
};
const BEDS = ["Studio", "1", "2", "3", "4", "5+"];
const BATHS = ["1", "2", "3", "4+"];
const BED_VAL = { "Studio": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5+": 5 };
const BATH_VAL = { "1": 1, "2": 2, "3": 3, "4+": 4 };
const HAZARD_WORDS = ["hazard", "biohazard", "mold", "mould", "asbestos", "scaffold", "ladder", "height", "moisissure", "amiante", "échafaud", "hauteur", "dangereux"];

const fieldCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-steel-light/60 transition-all";
const selectCls = fieldCls + " appearance-none select-chevron";

function Field({ label, children, required }) {
  return (
    <div>
      <label className="text-sm text-white/50 mb-2 block">
        {label} {required && <span className="text-steel-light">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function Calculator() {
  const { t } = useLang();
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", postal_code: "",
    service: "Standard Cleaning", property_type: "Apartment / Condo",
    bedrooms: "3", bathrooms: "2", sqft: 2000, quantity: 1,
    frequency: "One-time", condition: "Average", addons: [],
    timing: "This Week", contact_preference: "Phone", details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const startedRef = React.useRef(false);

  const set = (k, v) => {
    if (!startedRef.current) { startedRef.current = true; track("quote_started", {}); }
    setForm((p) => ({ ...p, [k]: v }));
  };
  const toggleAddon = (k) => setForm((p) => ({ ...p, addons: p.addons.includes(k) ? p.addons.filter((x) => x !== k) : [...p.addons, k] }));

  const svc = SERVICES[form.service];
  const kind = svc.kind;

  const chooseService = (s) => {
    const k = SERVICES[s].kind;
    setForm((p) => ({
      ...p, service: s, quantity: 1,
      property_type: k === "residential" || s === "Residential Window Cleaning" ? (TYPES.includes(p.property_type) && p.property_type !== "Office / Commercial Space" ? p.property_type : "House") : "Office / Commercial Space",
    }));
  };

  const { estimate, reviewReasons } = useMemo(() => {
    const sqft = Math.max(Number(form.sqft) || 0, 0);
    const qty = Math.max(Number(form.quantity) || 1, 1);
    let base;
    if (kind === "residential") {
      base = svc.base + ((BED_VAL[form.bedrooms] ?? 3) - 3) * 25 + ((BATH_VAL[form.bathrooms] ?? 2) - 2) * 25;
    } else if (kind === "sqft") {
      base = sqft * svc.rate;
    } else if (kind === "unit") {
      base = qty * svc.rate;
    } else {
      base = svc.base;
    }
    base *= CONDITIONS[form.condition] || 1;
    if (form.service === "Commercial Cleaning") base *= 1 - (FREQ_DISCOUNT[form.frequency] || 0);
    base = Math.max(base, svc.min ?? (kind === "residential" ? RES_MIN : 0));
    if (kind === "residential") base = Math.max(base, RES_MIN);
    base += form.addons.reduce((a, k) => a + (ADDONS[k] || 0), 0);

    const reasons = [];
    if (form.service === "Post-Construction Cleaning") reasons.push(t("Post-construction job"));
    if (form.condition === "Very heavy / major buildup") reasons.push(t("Very heavy condition reported"));
    if (form.service === "Commercial Cleaning" && sqft > 10000) reasons.push(t("Large commercial area (over 10,000 sq ft)"));
    if (form.service === "Floor Buffing / Waxing" && sqft > 5000) reasons.push(t("Large floor care area (over 5,000 sq ft)"));
    const details = (form.details || "").toLowerCase();
    if (HAZARD_WORDS.some((w) => details.includes(w))) reasons.push(t("Specialized equipment / height / hazardous work mentioned"));
    return { estimate: Math.round(base / 5) * 5, reviewReasons: reasons };
  }, [form, svc, kind, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim() || !form.postal_code.trim()) {
      toast.error(t("Please fill in your name, phone, email and postal code."));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t("Please enter a valid email address."));
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast.error(t("Please enter a valid phone number (10 digits)."));
      return;
    }
    if (!/^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(form.postal_code.trim())) {
      toast.error(t("Please enter a valid Canadian postal code (e.g. H2X 1Y4)."));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        sqft: kind === "sqft" ? Number(form.sqft) || 0 : null,
        quantity: kind === "unit" ? Number(form.quantity) || 1 : null,
        bedrooms: kind === "residential" ? form.bedrooms : null,
        bathrooms: kind === "residential" ? form.bathrooms : null,
      };
      const res = await fetch(`${API}/quote-requests`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setDone(data);
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
    } catch {
      toast.error(t("Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="bg-ink-950 min-h-screen flex items-center justify-center px-6 py-32">
        <Seo title="Quote Request Received" path="/quote" description="Your North Peak quote request has been received." />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-strong rounded-4xl p-8 md:p-12 max-w-xl w-full text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-steel/20 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-steel-light" />
          </div>
          <p className="overline text-steel-light mb-2">{t("Request Received")}</p>
          <h1 className="font-serif text-gradient text-3xl md:text-4xl mb-4" style={{ fontWeight: 500 }}>{t("Thank you! Your quote request is in.")}</h1>
          <p className="text-white/60 leading-relaxed mb-6">
            {t("We've emailed you a confirmation. A member of the North Peak team will contact you within one business day to confirm the details and your final price.")}
          </p>
          {done.needs_manual_review && (
            <div data-testid="quote-success-review" className="flex items-start gap-3 text-left rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 mb-6">
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
              <p className="text-amber-200/90 text-sm leading-relaxed">{t("Manual quote required — final pricing will be confirmed by North Peak Facility Services.")}</p>
            </div>
          )}
          <div className="glass rounded-2xl px-5 py-4 mb-3 flex items-center justify-between">
            <span className="text-white/50 text-sm">{t("Reference ID")}</span>
            <span data-testid="quote-reference-id" className="text-steel-light font-600">{done.reference_id}</span>
          </div>
          {done.estimate != null && !done.needs_manual_review && (
            <div className="glass rounded-2xl px-5 py-4 mb-8 flex items-center justify-between">
              <span className="text-white/50 text-sm">{t("Estimated starting price")}</span>
              <span className="text-white font-600 tabular-nums">{t("From")} CA${done.estimate}</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {done.token && (
              <Link to={`/proposal/${done.token}`} data-testid="quote-view-proposal" className="btn-pill inline-flex justify-center bg-white text-ink-950 px-8 py-3.5 text-sm font-600">{t("View My Quote & Status")}</Link>
            )}
            <Link to="/" data-testid="quote-success-home" className="btn-pill inline-flex justify-center glass text-white/80 px-8 py-3.5 text-sm font-600">{t("Back to Home")}</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="calculator-page" className="bg-ink-950">
      <Seo title="Get a Free Quote" path="/quote" description="Get an instant estimated starting price for residential or commercial cleaning in Montreal — standard, deep, move-out, carpet, window and post-construction cleaning." keywords="Free Cleaning Quote Montreal, Cleaning Cost Calculator Montreal, Cleaning Estimate Montreal" />
      <PageHero
        overline={t("Free Quote Calculator")}
        title={t("Get your personalized cleaning estimate.")}
        subtitle={t("Answer a few quick questions about your space. We'll use the information to provide a starting estimate and confirm your final pricing based on your specific needs.")}
        image={IMAGES.glassCurve}
      />

      <section className="pb-28 md:pb-36 -mt-8">
        <form onSubmit={submit} noValidate className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
          {/* ---------- FORM ---------- */}
          <Reveal className="lg:col-span-3">
            <div className="glass rounded-4xl p-6 md:p-9 space-y-8" data-testid="calculator-form">
              {/* Contact */}
              <div>
                <h2 className="text-white font-600 text-lg mb-5">{t("Your Information")}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t("First Name")} required>
                    <input data-testid="quote-first-name" className={fieldCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder={t("Jane")} autoComplete="given-name" />
                  </Field>
                  <Field label={t("Last Name")} required>
                    <input data-testid="quote-last-name" className={fieldCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder={t("Tremblay")} autoComplete="family-name" />
                  </Field>
                  <Field label={t("Phone")} required>
                    <input data-testid="quote-phone" type="tel" className={fieldCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(514) 555-0123" autoComplete="tel" inputMode="tel" />
                  </Field>
                  <Field label={t("Email")} required>
                    <input data-testid="quote-email" type="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" autoComplete="email" inputMode="email" />
                  </Field>
                  <Field label={t("Postal Code")} required>
                    <input data-testid="quote-postal" className={fieldCls} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="H2X 1Y4" autoComplete="postal-code" />
                  </Field>
                </div>
              </div>

              {/* Service + property */}
              <div>
                <h2 className="text-white font-600 text-lg mb-5">{t("Your Space")}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t("Service")}>
                    <select data-testid="quote-service" className={selectCls} value={form.service} onChange={(e) => chooseService(e.target.value)}>
                      {Object.entries(SERVICES).map(([k, v]) => <option key={k} value={k} className="bg-ink-800">{`${t(k)} — ${t(v.tag)}`}</option>)}
                    </select>
                  </Field>
                  <Field label={t("Property Type")}>
                    <select data-testid="quote-property-type" className={selectCls} value={form.property_type} onChange={(e) => set("property_type", e.target.value)}>
                      {TYPES.map((k) => <option key={k} value={k} className="bg-ink-800">{t(k)}</option>)}
                    </select>
                  </Field>
                  {kind === "residential" && (
                    <>
                      <Field label={t("Bedrooms")}>
                        <select data-testid="quote-bedrooms" className={selectCls} value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}>
                          {BEDS.map((k) => <option key={k} value={k} className="bg-ink-800">{k === "Studio" ? t("Studio") : k}</option>)}
                        </select>
                      </Field>
                      <Field label={t("Bathrooms")}>
                        <select data-testid="quote-bathrooms" className={selectCls} value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)}>
                          {BATHS.map((k) => <option key={k} value={k} className="bg-ink-800">{k}</option>)}
                        </select>
                      </Field>
                    </>
                  )}
                  {kind === "sqft" && (
                    <Field label={t("Square Footage")}>
                      <input data-testid="quote-sqft" type="number" min="0" step="100" className={fieldCls} value={form.sqft} onChange={(e) => set("sqft", e.target.value)} placeholder="2000" inputMode="numeric" />
                    </Field>
                  )}
                  {kind === "unit" && (
                    <Field label={t(svc.unitLabel)}>
                      <input data-testid="quote-quantity" type="number" min="1" step="1" className={fieldCls} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} inputMode="numeric" />
                    </Field>
                  )}
                  <Field label={t("Property Condition")}>
                    <select data-testid="quote-condition" className={selectCls} value={form.condition} onChange={(e) => set("condition", e.target.value)}>
                      {Object.keys(CONDITIONS).map((k) => <option key={k} value={k} className="bg-ink-800">{t(k)}</option>)}
                    </select>
                  </Field>
                </div>
                {form.service === "Residential Window Cleaning" && (
                  <p className="mt-3 text-white/40 text-xs">{t("Covers a standard home with up to 20 windows.")}</p>
                )}
              </div>

              {/* Frequency */}
              <div>
                <label className="text-sm text-white/50 mb-3 block">{t("Cleaning Frequency")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {FREQS.map((k) => (
                    <button
                      key={k} type="button" data-testid={`quote-freq-${k}`} onClick={() => set("frequency", k)}
                      className={`px-3 py-3 rounded-2xl border text-sm font-500 transition-all ${form.frequency === k ? "bg-white text-ink-950 border-white" : "border-white/10 text-white/70 hover:border-white/30"}`}
                    >
                      {t(k)}
                    </button>
                  ))}
                </div>
                {form.service === "Commercial Cleaning" && (
                  <p className="mt-3 text-white/40 text-xs">{t("Commercial cleaning: Weekly saves 10%, Bi-weekly saves 5%.")}</p>
                )}
              </div>

              {/* Add-ons */}
              <div>
                <label className="text-sm text-white/50 mb-3 block">{t("Optional Add-ons")}</label>
                <div className="flex flex-wrap gap-2.5">
                  {Object.entries(ADDONS).map(([k, v]) => {
                    const on = form.addons.includes(k);
                    return (
                      <button
                        key={k} type="button" data-testid={`quote-addon-${k}`} onClick={() => toggleAddon(k)}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-500 transition-all ${on ? "bg-steel/25 border-steel-light/50 text-white" : "border-white/10 text-white/60 hover:border-white/30"}`}
                      >
                        <span className={`h-4 w-4 rounded-full flex items-center justify-center ${on ? "bg-steel-light text-ink-950" : "border border-white/25"}`}>{on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}</span>
                        {t(k)} <span className="text-white/40">+${v}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Timing + contact preference */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("How soon do you need cleaning?")}>
                  <select data-testid="quote-timing" className={selectCls} value={form.timing} onChange={(e) => set("timing", e.target.value)}>
                    {["ASAP", "This Week", "Next Week", "Within the Month", "Just Comparing Options"].map((k) => <option key={k} value={k} className="bg-ink-800">{t(k)}</option>)}
                  </select>
                </Field>
                <Field label={t("How would you prefer we contact you?")}>
                  <select data-testid="quote-contact-pref" className={selectCls} value={form.contact_preference} onChange={(e) => set("contact_preference", e.target.value)}>
                    {["Phone", "Text", "Email"].map((k) => <option key={k} value={k} className="bg-ink-800">{t(k)}</option>)}
                  </select>
                </Field>
              </div>

              {/* Details */}
              <Field label={t("Additional Details")}>
                <textarea data-testid="quote-details" rows={4} className={fieldCls + " resize-none"} value={form.details} onChange={(e) => set("details", e.target.value)} placeholder={t("Anything else we should know — access instructions, pets, priority areas, special requests…")} />
              </Field>
            </div>
          </Reveal>

          {/* ---------- ESTIMATE ---------- */}
          <Reveal delay={0.12} className="lg:col-span-2 lg:sticky lg:top-28">
            <div data-testid="quote-estimate-panel" className="relative glass-strong rounded-4xl p-7 md:p-9 overflow-hidden">
              <div className="absolute -top-20 -right-16 w-52 h-52 rounded-full bg-steel/25 blur-3xl pointer-events-none" />
              <div className="relative z-10">
                <div className="glass inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-6">
                  <CalcIcon className="h-3.5 w-3.5 text-steel-light" />
                  <span className="text-xs text-white/70 font-500 tracking-wide">{t("Your instant estimate")}</span>
                </div>

                <p className="overline text-steel-light mb-2">{reviewReasons.length > 0 ? t("Pricing") : t("Estimated Starting Price")}</p>
                {reviewReasons.length > 0 ? (
                  <div data-testid="quote-review-notice" className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-5 py-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-200 font-600 text-sm mb-1.5">{t("Manual quote required")}</p>
                        <p className="text-amber-200/80 text-xs leading-relaxed">{t("Manual quote required — final pricing will be confirmed by North Peak Facility Services.")}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={estimate}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
                        data-testid="quote-estimate-value"
                        className="font-serif text-gradient text-5xl md:text-6xl leading-none tabular-nums" style={{ fontWeight: 500 }}
                      >
                        {t("From")} CA${estimate.toLocaleString()}
                      </motion.div>
                    </AnimatePresence>
                    <p className="mt-3 text-white/50 text-sm">{t("Estimated starting price")} · {form.frequency === "One-time" ? t("per service") : t("per visit")}</p>
                  </>
                )}

                <div className="mt-6 space-y-2 text-sm">
                  {[[t("Service"), t(form.service)], [t("Property"), t(form.property_type)], [t("Frequency"), t(form.frequency)]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-3 py-2 border-b border-white/8">
                      <span className="text-white/45">{k}</span><span className="text-white/85 text-right">{v}</span>
                    </div>
                  ))}
                  {form.addons.length > 0 && (
                    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/8">
                      <span className="text-white/45">{t("Add-ons")}</span>
                      <span className="text-white/85 text-right">{form.addons.map((a) => t(a)).join(", ")}</span>
                    </div>
                  )}
                </div>

                <p className="mt-5 text-white/40 text-xs leading-relaxed">
                  {t("This is an estimated starting price — not a final quote. Final pricing is confirmed by North Peak Facility Services after reviewing the job requirements.")}
                </p>

                <button
                  type="submit" data-testid="quote-submit" disabled={submitting}
                  className="btn-pill group mt-6 w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20 disabled:opacity-60"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("Sending…")}</> : <>{t("Request This Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
                </button>
                <p className="mt-3 text-center text-white/40 text-xs">{t("No obligation. We respond within one business day.")}</p>

                <div className="mt-6 pt-6 border-t border-white/8 flex items-center justify-between gap-3">
                  <span className="text-white/45 text-xs">{t("Managing a large facility?")}</span>
                  <Link to="/commercial" data-testid="quote-enterprise-link" className="inline-flex items-center gap-1.5 text-steel-light text-xs font-600 whitespace-nowrap">
                    <Building2 className="h-3.5 w-3.5" /> {t("Commercial Services")}
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </form>
      </section>
    </div>
  );
}
